import React, { useMemo } from 'react';
import { useLocale } from '../contexts/LocaleContext';

// Lightweight FAQ using native <details> to avoid heavy re-renders from third-party accordions.
// Uses translations from en.json / es.json instead of an in-file array.
const FrequentQuestions: React.FC = () => {
  const { t } = useLocale();

  // Memoize items so map doesn't recreate entries on every render
  const items = useMemo(() => (t?.faq?.items || []), [t]);

  return (
    <section id="faq" className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-primary-dark mb-6">
            {t?.faq?.title || 'Frequently Asked Questions'}
          </h2>
          {t?.faq?.subtitle && (
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t.faq.subtitle}
            </p>
          )}
        </div>

        <div className="space-y-4">
          {items.map((it: any, idx: number) => (
            <details
              key={it.key || idx}
              className="group rounded-lg border border-gray-200 shadow-sm overflow-hidden"
            >
              <summary className="cursor-pointer px-6 py-4 list-none flex items-center justify-between gap-4">
                <div className="text-left">
                  <h3 className="text-lg font-semibold text-primary-dark">
                    {it.q}
                  </h3>
                </div>
                <div className="text-gray-400 transition-transform duration-200 group-open:rotate-45">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M12 5v14" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M5 12h14" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              </summary>
              <div className="px-6 pb-6 text-gray-700 leading-relaxed bg-white">
                <p>{it.a}</p>
              </div>
            </details>
          ))}
        </div>

        <div className="mt-16 text-center">
          <div className="bg-gradient-primary rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">
              {t?.faq?.ctaTitle || 'Still Have Questions?'}
            </h3>
            <p className="text-white/90 mb-6">
              {t?.faq?.ctaSubtitle || 'Our team is here to help you understand how Aizora can benefit your specific business needs.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#contact"
                className="inline-flex items-center justify-center px-6 py-3 bg-white text-primary-dark rounded-lg font-semibold hover:bg-white/90 transition-colors duration-300"
              >
                {t?.promo?.cta || 'Schedule a Demo'}
              </a>
              <a
                href={`mailto:${t?.contact?.email || 'contact@aizora.com'}`}
                className="inline-flex items-center justify-center px-6 py-3 border border-white text-white rounded-lg font-semibold hover:bg-white/10 transition-colors duration-300"
              >
                {t?.contact?.orReach || 'Contact Support'}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default React.memo(FrequentQuestions);
